import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { X, PhoneCall, Sun, CloudSun, Moon, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SESSION_KEY = "msc_callback_popup_seen";

const SLOTS = [
  { id: "matin", label: "Matin", hint: "9h–12h", icon: Sun },
  { id: "apres_midi", label: "Après-midi", hint: "12h–17h", icon: CloudSun },
  { id: "fin_journee", label: "Fin de journée", hint: "17h–20h", icon: Moon },
];

export default function RetentionPopup() {
  const { settings: s } = useSettings();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", slot: "matin" });
  const formStartRef = useRef(Date.now());
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!s?.popup_callback_enabled) return;
    // Once per session
    if (s.popup_callback_one_per_session !== false && sessionStorage.getItem(SESSION_KEY)) return;

    const trigger = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      setOpen(true);
      formStartRef.current = Date.now();
      sessionStorage.setItem(SESSION_KEY, "1");
    };

    let tid;
    const delay = s.popup_callback_delay_seconds ?? 30;
    if (delay > 0) {
      tid = setTimeout(trigger, delay * 1000);
    }

    const onMouseLeave = (e) => {
      if (!s.popup_callback_exit_intent) return;
      // Exit intent : user moves mouse above viewport
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      if (tid) clearTimeout(tid);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [s]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Prénom et téléphone requis");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email || "",
        message: "",
        type: "rappel",
        source: "popup_retention",
        page: window.location.pathname,
        form_started_at: formStartRef.current,
        meta: { callback_slot: form.slot, popup_origin: "retention" },
      });
      setDone(true);
      setTimeout(() => setOpen(false), 2800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur — réessayez");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" onClick={() => setOpen(false)} data-testid="retention-popup">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="w-7 h-7 text-emerald-600" strokeWidth={3} />
            </div>
            <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">Demande reçue !</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Nous vous rappelons très vite.</p>
          </div>
        ) : (
          <>
            {s?.popup_callback_image ? (
              <div className="h-28 bg-cover bg-center" style={{ backgroundImage: `url(${s.popup_callback_image})` }} />
            ) : (
              <div className="h-24 bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] flex items-center justify-center">
                <PhoneCall className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
            )}
            <button type="button" onClick={() => setOpen(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-[color:var(--brand-navy)] shadow hover:bg-white" data-testid="retention-close">
              <X className="w-4 h-4" />
            </button>
            <form onSubmit={submit} className="p-6 space-y-3">
              <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">{s?.popup_callback_title || "Vous avez une question ?"}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{s?.popup_callback_subtitle || "Laissez-nous vos coordonnées, nous vous rappelons sous 24h."}</p>
              <div className="space-y-2 pt-2">
                <div>
                  <Label className="text-xs">Prénom *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Votre prénom" data-testid="retention-name" autoFocus />
                </div>
                <div>
                  <Label className="text-xs">Téléphone *</Label>
                  <Input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="retention-phone" />
                </div>
                <div>
                  <Label className="text-xs">Email <span className="text-[hsl(var(--muted-foreground))]">(optionnel)</span></Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.fr" data-testid="retention-email" />
                </div>
                <div>
                  <Label className="text-xs">Meilleur moment pour être rappelé(e)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {SLOTS.map(sl => {
                      const on = form.slot === sl.id;
                      return (
                        <button
                          key={sl.id}
                          type="button"
                          onClick={() => setForm({ ...form, slot: sl.id })}
                          className={`p-2 rounded-lg border-2 transition-all ${on ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/10" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/40"}`}
                          data-testid={`retention-slot-${sl.id}`}
                        >
                          <sl.icon className={`w-4 h-4 mx-auto ${on ? "text-[color:var(--brand-sky)]" : "text-[hsl(var(--muted-foreground))]"}`} />
                          <div className={`text-[11px] font-bold mt-1 ${on ? "text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))]"}`}>{sl.label}</div>
                          <div className="text-[9px] text-[hsl(var(--muted-foreground))]">{sl.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 bg-[color:var(--brand-navy)] font-bold mt-2" data-testid="retention-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                Me faire rappeler
              </Button>
              <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))]">En soumettant, vous acceptez notre <a href="/confidentialite" className="underline">politique de confidentialité</a>.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
